import { cardPayOnlineLabel, channelName, parseMoneyToCents, printRegistersOf, qrPayLabel, tt, type FilterChannel } from '@agos/shared'

/**
 * The *Sprievodný list* a driver carries — §9.108, `BACKEND.md` §10A.35's `print-note`.
 *
 * <p><b>Ported line for line from legacy's `buildDeliveryNoteLines`.</b> A driver reads this at speed
 * in a car, and changing its shape would change what somebody is used to for no reason. The rule, the
 * order number, the date, the časenka, the customer, a picking summary, the items, the money, the
 * payment method, the note, the driver.
 *
 * <p><b>It is not a fiscal document.</b> `print-note` is documented as *"a slip that is not a fiscal
 * document"* — nothing is registered and no receipt is issued, which is why it carries no VAT
 * recapitulation and no document number.
 *
 * <p><b>Every field is optional and each is omitted rather than printed empty.</b> A line reading
 * `Adr: undefined` on a slip somebody is using to find a house is worse than no line.
 */

/**
 * Thirty-two columns, which is what the register's paper takes.
 *
 * <p><b>The server truncates at forty.</b> `FiskalDocument.NAME_WIDTH` is 40 and `clean()` cuts to it,
 * so a long address or product name is shortened on the slip rather than wrapped — legacy behaves the
 * same way. The rules are 32 so they sit inside that with room to spare.
 */
const RULE = '================================'
const THIN = '--------------------------------'

export interface NoteItem {
  productName?: string
  quantity?: number
  unitPrice?: number | string
  totalPrice?: number | string
  categoryName?: string | null
  /**
   * The burger box's slot this line filled, or null for a line in no box - §9.732. `Príloha`, `Dressing`,
   * `nápoje` and `Nápoj` on the owner's data. On every line of `GET /orders`, which the board reads.
   */
  boxSlotLabel?: string | null
  /**
   * The row id of the line this one was ordered with as an upsell add-on, or null - §9.732, on
   * `BACKEND.md` §10A.366. On every line of `GET /orders` too.
   */
  addonOfId?: number | null
}

export interface NoteOrder {
  orderNumber?: string
  createdAt?: string
  scheduledAt?: string | null
  customerName?: string
  customerPhone?: string
  customerAddress?: string | null
  total?: number | string
  deliveryCost?: number | string
  discount?: number | string
  paymentMethod?: string
  notes?: string | null
  driverName?: string
  items?: NoteItem[]
}

const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Hotovost',
  card_terminal: tt('Karta terminal'),
  card_courier: tt('Karta kurier'),
  // §9.767, owner O129 - the online card's one name, which is ASCII and so prints as it is on the slip.
  card_online: cardPayOnlineLabel(),
  // §9.741 - a door QR payment, filed as `qr_pay` by §10A.390. Legacy's map is the four above and the
  // card; an unnamed method prints as itself, so the slip read *Platba: qr_pay*.
  qr_pay: qrPayLabel(),
  card: tt('Karta'),
}

/**
 * What the summary calls out, in the order it prints - §9.732, owner O102.
 *
 * <p>Legacy's `getCategoryProductSummary` singles out sides, dressings and drinks because they are what
 * gets left behind: a pizza in the bag is obvious, a missing dressing is not.
 *
 * <p><b>Legacy's own three patterns, matched anywhere in a lower-cased name.</b> The port had anchored
 * them to the start (`/^dresing/`) and dropped `side`, `dressing`, `drink` and `beverage`, so *Omáčky a
 * dresingy* or *Nealko nápoje* fell out of the summary, and a box line whose slot reads `Dressing` was
 * matched by nothing. The fourth group is AGOS's own: an upsell add-on none of the three places.
 */
export type SummaryGroup = 'side' | 'dressing' | 'drink' | 'addon'

const SUMMARY_PATTERNS: readonly { group: Exclude<SummaryGroup, 'addon'>; pattern: RegExp }[] = [
  { group: 'side', pattern: /pr[ií]loh|side/ },
  { group: 'dressing', pattern: /dres(s?)ing/ },
  { group: 'drink', pattern: /n[aá]poj|drink|beverage/ },
]

const SUMMARY_ORDER: readonly SummaryGroup[] = ['side', 'dressing', 'drink', 'addon']

function money(value: number | string | undefined): string {
  const amount = Number(value ?? 0)
  // N-02 is about screens. This string is a register command: `FiskalDocument.note` joins its parts
  // with a comma and `clean()` replaces every comma in a line with a space, so a Slovak decimal comma
  // would print as `24 50 EUR`. Legacy writes a dot for the same reason.
  // eslint-disable-next-line no-restricted-syntax
  return `${(Number.isFinite(amount) ? amount : 0).toFixed(2)} EUR`
}

function positive(value: number | string | undefined): boolean {
  return Number(value ?? 0) > 0
}

/** `dd.mm.yyyy hh:mm` in the restaurant's own locale, blank when there is no date to print. */
function stamp(iso: string | undefined | null, withWeekday = false): string {
  if (!iso) return ''
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return ''
  // N-01 is honoured rather than dodged — the zone is pinned on the next line. The `@agos/shared`
  // helpers format for a screen; this is a 40-column printer line with its own shape.
  // eslint-disable-next-line no-restricted-syntax
  return new Intl.DateTimeFormat('sk-SK', {
    // **Pinned, and this is the half of the port that legacy got wrong.** Legacy omits `timeZone`, so
    // the slip carries whatever zone the manager's machine is on — a laptop left on UTC prints a
    // delivery two hours early. The restaurant's day is a Bratislava day.
    timeZone: 'Europe/Bratislava',
    ...(withWeekday ? { weekday: 'long' as const } : { year: '2-digit' as const }),
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(when)
}

/** The first of legacy's groups a name falls in, matched anywhere in it lower-cased, or null. */
function groupNamedBy(text: string | null | undefined): SummaryGroup | null {
  const lower = (text ?? '').toLowerCase()
  return SUMMARY_PATTERNS.find((entry) => entry.pattern.test(lower))?.group ?? null
}

/**
 * Which part of the summary a line belongs to, or null for a line it leaves out - §9.732.
 *
 * <p>The first that answers: the line's **category** (legacy's rule); its **burger-box slot**, because the
 * student box's lines sit in *Gratis veci* and are told apart only by the slot; its **own name**, which is
 * what catches `🎁 DARČEK: Nápoj grátis` and `Darček Cesnakový dresing` - a gift is in no matching category
 * and is exactly what a driver must hand over. And then **an upsell add-on is listed anyway**, under the
 * other add-ons: the owner's report is that the summary stopped counting what the upsell added, and a
 * franchise's add-on can sit in a category no pattern names.
 */
export function summaryGroupOf(item: NoteItem): SummaryGroup | null {
  return (
    groupNamedBy(item.categoryName) ??
    groupNamedBy(item.boxSlotLabel) ??
    groupNamedBy(item.productName) ??
    (typeof item.addonOfId === 'number' ? 'addon' : null)
  )
}

/**
 * Sides, dressings, drinks and the other add-ons, in that order, each counted by name as `2x Hranolky` -
 * §9.732.
 *
 * <p><b>Counted by name, as legacy counts</b>: two lines of one dressing - two burgers that each took a dip -
 * are one line, `2x`, which is what the driver checks the bag against. A line with no name or no quantity
 * is skipped, as legacy's `aggregate` skips it.
 */
export function summaryLines(items: readonly NoteItem[]): string[] {
  const counted = new Map<SummaryGroup, Map<string, number>>()
  for (const item of items) {
    const group = summaryGroupOf(item)
    const name = (item.productName ?? '').trim()
    const quantity = Number(item.quantity ?? 0)
    if (group === null || name === '' || !(quantity > 0)) continue
    const names = counted.get(group) ?? new Map<string, number>()
    names.set(name, (names.get(name) ?? 0) + quantity)
    counted.set(group, names)
  }
  return SUMMARY_ORDER.flatMap((group) =>
    [...(counted.get(group) ?? [])].map(([name, quantity]) => `  ${quantity}x ${name}`),
  )
}

export function deliveryNoteLines(order: NoteOrder): string[] {
  const items = order.items ?? []
  const lines: string[] = [RULE, tt('    SPRIEVODNÝ LIST'), RULE]

  lines.push(`Obj: ${order.orderNumber ?? ''}`)
  const created = stamp(order.createdAt)
  if (created !== '') lines.push(`${tt('Dátum: ')}${created}`)
  const scheduled = stamp(order.scheduledAt, true)
  if (scheduled !== '') lines.push(`${tt('Časenka: ')}${scheduled}`)

  lines.push(THIN)
  const hasCustomer =
    Boolean(order.customerName) || Boolean(order.customerPhone) || Boolean(order.customerAddress)
  if (order.customerName) lines.push(order.customerName)
  if (order.customerPhone) lines.push(`Tel: ${order.customerPhone}`)
  if (order.customerAddress) lines.push(`Adr: ${order.customerAddress}`)
  if (hasCustomer) lines.push(THIN)

  const summary = summaryLines(items)
  if (summary.length > 0) {
    if (!hasCustomer) lines.push(THIN)
    lines.push(tt('SÚHRN:'))
    lines.push(...summary)
    lines.push(THIN)
  }

  for (const item of items) {
    // `totalPrice` where the server sent one, the multiplication where it did not — legacy's rule,
    // and the two disagree on a discounted line.
    const price =
      item.totalPrice ?? Number(item.unitPrice ?? 0) * Number(item.quantity ?? 0)
    lines.push(`${item.quantity ?? 0}x ${item.productName ?? ''}`)
    lines.push(`   ${money(price)}`)
  }

  if (positive(order.deliveryCost)) lines.push(`Doprava: ${money(order.deliveryCost)}`)
  if (positive(order.discount)) lines.push(`${tt('Zľava: -')}${money(order.discount)}`)

  lines.push(THIN)
  lines.push(`SPOLU: ${money(order.total)}`)
  lines.push(`Platba: ${PAYMENT_LABELS[order.paymentMethod ?? ''] ?? order.paymentMethod ?? ''}`)
  if (order.notes) lines.push(`Pozn: ${order.notes}`)
  if (order.driverName) lines.push(`${tt('Šofér: ')}${order.driverName}`)
  lines.push(RULE)

  return lines
}

/** The body `POST /fiskal/print-note` reads. Delivery's register, as legacy sends. */
/**
 * The longest line the register's endpoint accepts - §9.417, FB-744.
 *
 * `FiskalController:583` declares `List<@NotNull @Size(max = 100) String> lines`, so **one over-long
 * line makes the server refuse the whole slip.** The owner hit it on order 15903: *Vytlačené: 0
 * nevytlačené: 15903 - size must be between 0 and 100*, which they only saw at all because the bare
 * `catch` above it was fixed first.
 *
 * Named here rather than written as `100` at the wrap, so the number and the reason for it stay
 * together.
 */
const MAX_LINE = 100

/**
 * One line, wrapped to what the register accepts - §9.417.
 *
 * **Wrapped at a space rather than truncated.** Every line that can run long is typed by somebody -
 * an address with delivery instructions, a custom pizza's name, a customer's note - and cutting one
 * off loses the house number or the last topping. A slip is ~32 to 48 characters wide on the paper
 * anyway, so wrapping is what the printer wants regardless of the limit.
 *
 * A single unbroken word longer than the limit is cut, because there is nothing else to do with it
 * and refusing the whole slip over one is what this fixes.
 */
function wrapped(line: string): string[] {
  if (line.length <= MAX_LINE) return [line]
  const out: string[] = []
  let rest = line
  while (rest.length > MAX_LINE) {
    const cut = rest.lastIndexOf(' ', MAX_LINE)
    const upTo = cut > 0 ? cut : MAX_LINE
    out.push(rest.slice(0, upTo).trimEnd())
    rest = rest.slice(cut > 0 ? upTo + 1 : upTo)
  }
  if (rest !== '') out.push(rest)
  return out
}

/**
 * The request, with every line inside the server's limit.
 *
 * **Wrapped here rather than in `deliveryNoteLines`**, because this is the function bound by the
 * constraint: the content is the same slip either way, and a preview that shows an over-long line
 * is honest about what was typed.
 */
export function printNotePayload(
  order: NoteOrder,
  /**
   * §9.740 - the register the slip comes out of: the order's receipt's, so a bag's two sheets come out of one
   * device. {@link registerFor} answers it. **Required**, so no call site prints on a register it did not choose.
   */
  kasaId: string,
): { kasaId: string; lines: string[] } {
  return { kasaId, lines: deliveryNoteLines(order).flatMap(wrapped) }
}

/**
 * What the toolbar says after printing a batch.
 *
 * <p><b>A failure is named, not folded into a total.</b> The register is reachable or it is not, and
 * "3 z 5" with no list leaves the dispatcher guessing which two drivers are about to leave without a
 * slip.
 */
export function printOutcomeNote(
  printed: number,
  failed: readonly string[],
  /**
   * What the register said, once - §9.417, FB-744.
   *
   * **The owner read this toast three times and it never told them anything.** *Vytlačené: 0 ·
   * nevytlačené: #15903* came from a bare `catch {}` that discarded the error entirely, so the
   * register could have said *paper out*, *unreachable* or a validation sentence and the dispatcher
   * saw the order number and nothing else.
   *
   * The swallowed-refusal sweeps (§9.379, §9.385, FB-704) missed it because they looked for handlers
   * reading the **wrong** property off an error. A `catch` with no parameter has nothing to read, so
   * a grep for a wrong property name cannot see it - `agos-a-ratchets-pattern-is-its-blind-spot`.
   *
   * Said once rather than per order: a batch fails for one reason far more often than for eight, and
   * eight copies of *Tlačiareň neodpovedá* is a toast nobody finishes reading. Optional, so the
   * tests written before it read the same.
   */
  reason = '',
): string {
  if (failed.length === 0) {
    return `${tt('Vytlačené sprievodné listy:')} ${printed}`
  }
  const why = reason.trim() === '' ? '' : ` - ${reason.trim()}`
  return `${tt('Vytlačené:')} ${printed} · ${tt('nevytlačené:')} ${failed.join(', ')}${why}`
}

/**
 * The receipt request for one delivery order — §9.123, the owner's point 9.
 *
 * <p><b>This issues a real tax document.</b> `BLOCKED.md` requires `POST /fiskal/receipt` to be
 * intercepted in every e2e, which is why the payload is built here as a pure value rather than inline
 * at the call: what is sent to a register is worth being able to read and test without sending it.
 *
 * <p><b>No `vatRate` is sent.</b> `BACKEND.md §10A.67` made the product catalogue outrank whatever
 * the client claims, precisely because clients disagreed about whether a channel is eaten in. Sending
 * a rate here would be ignored at best and a contradiction in the record at worst.
 *
 * <p><b>`kasaId` is the delivery register</b>, as legacy sends from this board — a dispatch screen is
 * the rozvoz till by definition. **§9.740, owner O110 - unless the order's channel names its registers**: a
 * channel both on the premises and driven may (Sereď's *Osobný odber* prints on *Kasa reštaurácia* as well as on
 * the delivery registers), and then {@link printRegisterPlan} decides and the caller passes the answer.
 *
 * <p><b>Answers `null` where the order does not say how it was paid.</b> A tax document states the
 * payment method; defaulting it would print a guess on a real document. The caller names the order
 * as a failure instead, which is what the dispatcher needs in order to go and look at it.
 */
export function receiptPayloadFor(
  order: NoteOrder & { id?: number; type?: string },
  /** §9.740 - {@link registerFor}'s answer. Required for the reason {@link printNotePayload}'s is. */
  kasaId: string,
): {
  kasaId: string
  orderId: number
  orderNumber: string
  orderType?: string
  paymentMethod: string
  total: number
  discount?: number
  deliveryCost?: number
  items: { name: string; quantity: number; unitPrice: number }[]
} | null {
  const money = (value: number | string | undefined): number =>
    value === undefined ? 0 : parseMoneyToCents(value) / 100
  const paymentMethod = (order.paymentMethod ?? '').trim()
  if (paymentMethod === '') return null
  // §9.417 - **`order.id ?? 0` was a fallback on a fiscal request.** An order with no id would have
  // asked the register for a tax document against **order 0**. Nothing on this data reaches it, and a
  // silent wrong order number on a tax document is not something to leave to a `??`.
  if (order.id === undefined || order.id === null || order.id <= 0) return null
  const discount = money(order.discount)
  const deliveryCost = money(order.deliveryCost)
  return {
    kasaId,
    orderId: order.id,
    orderNumber: order.orderNumber ?? '',
    ...(order.type === undefined ? {} : { orderType: order.type }),
    paymentMethod,
    total: money(order.total),
    // Omitted rather than sent as zero: a nil discount is not a discount of nothing.
    ...(discount > 0 ? { discount } : {}),
    ...(deliveryCost > 0 ? { deliveryCost } : {}),
    items: (order.items ?? []).map((item) => ({
      name: (item.productName ?? '').trim(),
      quantity: item.quantity ?? 0,
      unitPrice: money(item.unitPrice),
    })),
  }
}

/**
 * What the toolbar says after issuing a batch of receipts.
 *
 * <p>Named failures for the same reason `printOutcomeNote` names them, only harder: an unissued
 * receipt is a sale with no tax document, and the dispatcher has to know which order to chase.
 */
/** What a batch of prints reported about the register itself - §9.500. */
export interface BatchRegisterReport {
  /** Distinct warnings worth showing, in the order they first appeared. */
  warnings: string[]
  /** How many orders the register had no paper for. */
  paperOut: number
}

/**
 * Collapse a batch's per-order register reports into what the operator is told once - §9.500.
 *
 * <p><b>Distinct, and once, on purpose.</b> Eight orders on a register with no paper would otherwise raise
 * eight identical toasts, which is the failure `isTransientKasaWarning`'s own javadoc names - *"trains staff
 * to dismiss register warnings without reading them"* - reached by volume instead of by content.
 *
 * <p><b>The paper count is kept rather than reduced to a flag</b>, because *the paper ran out* is a different
 * problem on one order and on eight: the first is one receipt to collect, the second is a batch nobody has.
 *
 * <p>Order is preserved so the first thing that went wrong is the first thing read.
 */
export function batchRegisterReport(
  reports: readonly { warning: string | null; paperOut: boolean }[],
): BatchRegisterReport {
  const warnings: string[] = []
  let paperOut = 0
  for (const report of reports) {
    if (report.warning !== null && !warnings.includes(report.warning)) warnings.push(report.warning)
    if (report.paperOut) paperOut += 1
  }
  return { warnings, paperOut }
}

/**
 * Whether a finished batch print is something the operator has to look at - §9.499.
 *
 * <p><b>A named predicate rather than three clauses at the call site</b>, because the interesting arm has
 * nothing to do with failure: a batch where <b>every</b> order already had its document issued **no fiscal
 * document at all**, and it used to be styled as a success while a delivery slip came out of the printer.
 * That is the owner's report of 2026-09-26. A condition living inline in a component is one no test can see
 * and one a later edit can drop silently - which `agos-a-test-on-one-side-of-a-seam` has cost this
 * repository four times.
 *
 * <p><b>`alreadyHad` on its own is deliberately quiet.</b> When some receipt printed, the rest are §9.362's
 * case exactly - the document is on the tray and there is nothing to chase - so colouring the whole batch
 * red would teach the operator to ignore the colour.
 */
export function batchNeedsAttention(counts: {
  issued: number
  failed: number
  refused: number
  alreadyHad: number
}): boolean {
  if (counts.failed > 0 || counts.refused > 0) return true
  // Nothing issued and something already documented: the press produced no fiscal document.
  return counts.issued === 0 && counts.alreadyHad > 0
}

export function receiptOutcomeNote(
  issued: number,
  failed: readonly string[],
  /**
   * The orders whose document the server said **already exists** - §9.362, FB-649.
   *
   * A third bucket, because it belongs in neither of the other two. Counting these as failures is
   * what this function's own javadoc warns against - *the dispatcher has to know which order to
   * chase* - and an order with a receipt on the tray is the one order there is nothing to chase
   * about. Counting them as issued would be true of the document and false of this batch, and a
   * dispatcher comparing the number against how many they selected would be left guessing.
   *
   * Optional so the two callers that have no such case, and the tests written before it existed,
   * read the same as before.
   */
  alreadyHad: readonly string[] = [],
  /**
   * The orders the **board itself** refused, and why - §9.417, FB-744 report 2.
   *
   * A fourth bucket, and the one the owner actually hit. `receiptPayloadFor` answers null when the
   * order carries no payment method, which is right: *"a tax document must say how the sale was paid,
   * and this one does not know."* But they used to join `failed`, so the toast read *Vytlačené: 0,
   * nevytlačené: #15903* - **a refusal with no reason**, beside genuine register failures, and the
   * one thing a dispatcher could act on was the one thing it did not say.
   *
   * Separate from `failed` because the two call for different actions: a register failure is *try
   * again or check the till*, and this is *fix the order*. Optional, so every existing caller and
   * every test written before it reads the same.
   */
  refused: readonly string[] = [],
  /**
   * What the register said about the first receipt that failed - `BACKEND.md` §10A.379.
   *
   * Said once, at the end, as `printOutcomeNote` says the slip batch's (§9.417). A receipt refused because
   * the register is switched off used to read *zlyhalo: #1, #2* and stop, which is the half of the owner's
   * *"aby tlačilo aj fiškálne doklady"* a screen could answer. Optional, so every caller and test written
   * before it reads the same.
   */
  reason = '',
): string {
  /* §9.499 - **named when this batch issued nothing, counted otherwise, and both halves are §9.362's own
   * reasoning rather than a reversal of it.** That entry chose a count deliberately: *"three numbers in a
   * toast about ten orders reads as a problem"*, and an order whose document is on the tray is the one there
   * is nothing to chase about. **True while some receipt printed.** When `issued` is 0 the opposite holds -
   * nothing printed, so every one of them is the chase list, and a bare count leaves a dispatcher holding a
   * slip unable to tell which order it belongs to. §9.417 made the identical correction to the refused
   * bucket. So the discriminator is `issued`, and each case keeps the wording its own argument asks for. */
  const already =
    alreadyHad.length === 0
      ? ''
      : issued === 0
        ? `, ${tt('už malo doklad')}: ${alreadyHad.join(', ')}`
        : `, ${alreadyHad.length} ${tt('už malo doklad')}`
  const blocked =
    refused.length === 0 ? '' : `. ${tt('Bez spôsobu platby')}: ${refused.join(', ')}`
  /* **And what to do, once, when this batch issued nothing at all.** That is the case the owner reported:
   * the slip prints (§9.362, deliberately - the sale is documented), the receipt does not, and the sentence
   * said neither. A reprint needs a storno first, which is the control immediately beside this button. */
  const howToReprint =
    issued === 0 && alreadyHad.length > 0
      ? `. ${tt('Doklad už existuje - nový sa dá vytlačiť až po stornovaní. Sprievodný list bol vytlačený.')}`
      : ''
  if (failed.length === 0 && refused.length === 0) {
    return `${tt('Vytlačené fiškálne doklady:')} ${issued}${already}${howToReprint}`
  }
  if (failed.length === 0) return `${issued} ${tt('vytlačené')}${already}${blocked}${howToReprint}`
  const why = reason.trim() === '' ? '' : ` - ${reason.trim()}`
  return `${issued} ${tt('vytlačené')}${already}, ${tt('zlyhalo')}: ${failed.join(', ')}${blocked}${howToReprint}${why}`
}

/**
 * The register the board prints on where a channel names none - legacy's own, because a dispatch screen is the
 * rozvoz till by definition (§9.123).
 */
export const BOARD_REGISTER = 'rozvoz'

/** A channel that names two or more registers, asked once for every order of it in the batch. */
export interface RegisterQuestion {
  slug: string
  name: string
  registers: readonly string[]
  orderIds: number[]
}

export interface RegisterPlan {
  /** Where an order prints when nobody has to choose. */
  fixed: ReadonlyMap<number, string>
  /** One question per channel that names more than one register, in the order the batch meets them. */
  ask: readonly RegisterQuestion[]
}

/**
 * Which register each order of a batch prints on - `FRONTEND.md` §9.740, `BACKEND.md` §10A.388, owner O110.
 *
 * **The owner (Sereď)**: *"osobný odber v Seredi sa tlačí aj na reštaurácii aj na rozvoze"*, and *Kasa rozvoz* and
 * *Kasa rozvoz Terminál* are one device that differs in whether it drives the card terminal - so *Tlačiť fiškálny
 * doklad* printing every receipt on `rozvoz` could never reach the other two.
 *
 * - **The channel names no register** (`printRegistersOf` - every delivery, and a pickup nobody configured): the
 *   board's own, `rozvoz`, as it always has.
 * - **It names one**: that one, with nothing to ask.
 * - **It names more**: one question per channel, however many of its orders are selected - a dispatcher printing
 *   eight bags of one channel answers once, not eight times.
 */
export function printRegisterPlan(
  orders: readonly { id?: number; type?: string }[],
  channels: readonly FilterChannel[],
): RegisterPlan {
  const fixed = new Map<number, string>()
  const asked = new Map<string, RegisterQuestion>()
  for (const order of orders) {
    const id = order.id ?? 0
    const slug = order.type ?? ''
    const listed = printRegistersOf(slug, channels)
    if (listed.length <= 1) {
      fixed.set(id, listed[0] ?? BOARD_REGISTER)
      continue
    }
    const question = asked.get(slug) ?? {
      slug,
      name: channelName(slug, channels),
      registers: listed,
      orderIds: [],
    }
    question.orderIds.push(id)
    asked.set(slug, question)
  }
  return { fixed, ask: [...asked.values()] }
}

/** Whether every question the plan asks has an answer among its own registers. */
export function planAnswered(plan: RegisterPlan, answers: Readonly<Record<string, string>>): boolean {
  return plan.ask.every((question) => question.registers.includes(answers[question.slug] ?? ''))
}

/**
 * The register one order prints on, once the questions are answered.
 *
 * An order the plan never met prints on the board's own register, which is the rule for a channel that names
 * none - the safest answer for an order nothing has decided about, because it is what the board did before.
 */
export function registerFor(
  order: { id?: number; type?: string },
  plan: RegisterPlan,
  answers: Readonly<Record<string, string>>,
): string {
  const fixed = plan.fixed.get(order.id ?? 0)
  if (fixed !== undefined) return fixed
  const answer = answers[order.type ?? '']
  const question = plan.ask.find((one) => one.slug === (order.type ?? ''))
  return answer !== undefined && question?.registers.includes(answer) === true ? answer : BOARD_REGISTER
}
